import { useAuth0 } from '@auth0/auth0-react';
import type { Product, ProductFilters } from '@my/directory-api';
import {
  useCreateProduct,
  useDeleteProduct,
  useOrganisation,
  useProducts,
  useUpdateProduct,
} from '@my/directory-api/hooks';
import { ConfirmDialog, ErrorBanner, Loading, PageHeader, showToast } from '@my/ui';
import { Edit, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DirectoryHistoryButton } from '@/components/HistoryButtons';
import { HazardousBadge } from './CataloguePickers';
import { ProductEditor } from './ProductEditor';
import {
  emptyDraft,
  hasErrors,
  toCreateRequest,
  toDraft,
  toUpdateRequest,
  validateDraft,
  type ProductDraft,
} from './lib/productDraft';

type KindFilter = 'all' | 'adr' | 'waste' | 'food' | 'feed';
const KINDS: KindFilter[] = ['all', 'adr', 'waste', 'food', 'feed'];

/** Active by default: an inactive product is one nobody should be picking. */
type StatusFilter = 'active' | 'inactive' | 'all';
const STATUSES: StatusFilter[] = ['active', 'inactive', 'all'];

/**
 * Settings → Products — the products this organisation carries, each with its
 * ADR Table A row and / or EU waste code.
 *
 * <p>
 * Sits under Central Configuration and shares its capability; it appears in
 * the menu once the organisation switches "Use Products" on (Settings → Cargo
 * Definitions). Reached with it off — a bookmark, a deep link — it still works, with
 * a notice on top: the switch hides products, it does not delete them.
 * </p>
 *
 * <p>
 * Products live in Directory, not TMS, because the catalogues they are checked
 * against are there and Depot will read the same list.
 * </p>
 */
export function ProductsPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['configuration', 'common']);
  const organisationQuery = useOrganisation(orgId);
  const switchedOff = organisationQuery.isSuccess && !organisationQuery.data.useProducts;

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={t('configuration:products.title')}
        description={t('configuration:products.description')}
      >
        <DirectoryHistoryButton
          organisationId={orgId}
          aggregateType="Product"
          wholeType
          subject={t('configuration:products.title')}
          variant="subtle"
        />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title={t('common:errors.noOrgLinkedTitle')}
            message={t('common:errors.noOrgLinkedMessage')}
          />
        )}

        {switchedOff && (
          <div className="flex items-start justify-between gap-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {t('configuration:products.switchedOffTitle')}
              </p>
              <p className="text-xs text-amber-800">
                {t('configuration:products.switchedOffMessage')}
              </p>
            </div>
            <Link
              to="/settings/cargo-definitions"
              className="shrink-0 text-xs font-medium text-purple-700 hover:underline"
            >
              {t('configuration:products.openConfiguration')}
            </Link>
          </div>
        )}

        <ProductsSection organisationId={orgId} />
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function ProductsSection({ organisationId }: { organisationId: string | null }) {
  const { t } = useTranslation(['configuration', 'common']);

  // ── Filters (server-side) ────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('active');

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchText.trim()), 250);
    return () => clearTimeout(id);
  }, [searchText]);

  const filters = useMemo<ProductFilters>(
    () => ({
      search: search || undefined,
      adr: kind === 'adr' ? true : undefined,
      waste: kind === 'waste' ? true : undefined,
      grade: kind === 'food' ? 'Food' : kind === 'feed' ? 'Feed' : undefined,
      status: status === 'active' ? 'Active' : status === 'inactive' ? 'Inactive' : undefined,
    }),
    [search, kind, status],
  );
  const isFiltered = !!search || kind !== 'all' || status !== 'active';
  const clearFilters = () => {
    setSearchText('');
    setSearch('');
    setKind('all');
    setStatus('active');
  };

  const listQuery = useProducts(organisationId, filters);
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // ── Editing ──────────────────────────────────────────────────────────────
  // `null` = no editor; `'new'` = drafting at the top; an id = editing that
  // row in place. Same lock-the-others behaviour as Nominal Codes.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ProductDraft>(emptyDraft());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Product | null>(null);
  const editorRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (editingId === null) return;
    const id = requestAnimationFrame(() => {
      editorRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [editingId]);

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyDraft());
    setSubmitted(false);
  };

  const startEdit = (row: Product) => {
    setEditingId(row.id);
    setDraft(toDraft(row));
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    setSubmitted(true);
    if (hasErrors(validateDraft(draft))) return;
    try {
      if (editingId === 'new') {
        await createMutation.mutateAsync(toCreateRequest(organisationId, draft));
        showToast({ type: 'success', message: t('products.added') });
      } else {
        await updateMutation.mutateAsync({
          organisationId,
          body: toUpdateRequest(editingId, draft),
        });
        showToast({ type: 'success', message: t('products.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('products.saveFailed'),
      });
    }
  };

  const confirmDelete = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('products.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('products.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const rows = listQuery.data ?? [];
  const COLS = 6;

  return (
    <>
      {/* ── Filters + search + add — the same strip as Resources ───────── */}
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} className={chipClass(kind === k)}>
            {t(`products.filters.${k}`)}
          </button>
        ))}

        <span className="h-4 w-px bg-slate-300" aria-hidden="true" />

        {STATUSES.map((st) => (
          <button
            key={st}
            type="button"
            onClick={() => setStatus(st)}
            className={chipClass(status === st)}
          >
            {t(`products.filters.${st}`)}
          </button>
        ))}

        {isFiltered && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-[10px] text-slate-400 underline hover:text-slate-600"
          >
            {t('products.filters.clear')}
          </button>
        )}

        <div className="flex-1" />

        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            aria-label={t('products.search')}
            placeholder={t('products.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-64 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Hidden while a row is being edited — never two drafts in flight. */}
        {editingId === null && (
          <button
            type="button"
            aria-label={t('products.addAria')}
            onClick={startNew}
            disabled={!organisationId || !listQuery.isSuccess}
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('common:actions.add')}
          </button>
        )}
      </div>

      {listQuery.isPending && <Loading label={t('products.loading')} />}
      {listQuery.isError && (
        <ErrorBanner title={t('products.loadFailed')} message={listQuery.error.message} />
      )}

      {listQuery.isSuccess && rows.length === 0 && editingId !== 'new' && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
          {isFiltered ? (
            <p className="text-sm text-slate-600">{t('products.noMatches')}</p>
          ) : (
            <>
              <p className="text-sm text-slate-600">{t('products.empty')}</p>
              <p className="mt-1 text-xs text-slate-500">{t('products.emptyHint')}</p>
            </>
          )}
        </div>
      )}

      {listQuery.isSuccess && (rows.length > 0 || editingId === 'new') && (
        <div className="overflow-hidden rounded-md">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full min-w-[860px] table-auto text-xs">
              <thead className="sticky top-0 z-10 bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">{t('products.table.name')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('products.table.adr')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('products.table.waste')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('products.table.grade')}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t('products.table.density')}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t('products.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300">
                {editingId === 'new' && (
                  <tr ref={editorRowRef}>
                    <td colSpan={COLS} className="bg-slate-50 p-3">
                      <ProductEditor
                        draft={draft}
                        onChange={setDraft}
                        submitted={submitted}
                        onSave={handleSave}
                        onCancel={cancel}
                        isBusy={isBusy}
                      />
                    </td>
                  </tr>
                )}

                {rows.map((row) => {
                  if (editingId === row.id) {
                    return (
                      <tr key={row.id} ref={editorRowRef}>
                        <td colSpan={COLS} className="bg-slate-50 p-3">
                          <ProductEditor
                            draft={draft}
                            onChange={setDraft}
                            submitted={submitted}
                            onSave={handleSave}
                            onCancel={cancel}
                            onDelete={() => setPendingDelete(row)}
                            isBusy={isBusy}
                          />
                        </td>
                      </tr>
                    );
                  }
                  const otherEditing = editingId !== null;
                  return (
                    <ProductRow
                      key={row.id}
                      row={row}
                      locked={otherEditing}
                      onEdit={() => startEdit(row)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('products.deleteDialog.title')}
        message={
          pendingDelete ? t('products.deleteDialog.message', { name: pendingDelete.name }) : null
        }
        confirmLabel={t('products.deleteDialog.confirm')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function ProductRow({
  row,
  locked,
  onEdit,
}: {
  row: Product;
  /** Another row (or +Add) is being edited — dim this one and refuse edits. */
  locked: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation('configuration');
  return (
    <tr
      className={locked ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}
      onDoubleClick={locked ? undefined : onEdit}
    >
      <td className="px-3 py-1.5 align-top">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900">{row.name}</span>
          {row.status === 'Inactive' && (
            <span className="inline-flex items-center rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
              {t('products.inactiveBadge')}
            </span>
          )}
        </div>
        {row.code && <div className="text-[11px] text-slate-500">{row.code}</div>}
      </td>
      <td className="px-3 py-1.5 align-top">
        {row.adrUnNumber ? (
          <div title={row.adrProperShippingName ?? undefined}>
            <div className="flex items-center gap-1.5">
              <span className="rounded-sm bg-orange-100 px-1.5 py-0.5 font-mono text-[11px] font-bold text-orange-800">
                {t('products.adr.un', { un: row.adrUnNumber })}
              </span>
              <span className="text-slate-700">
                {[row.adrClass, row.adrPackingGroup].filter(Boolean).join(' · ')}
              </span>
              {row.adrHazardNumber && (
                <span className="text-[11px] text-slate-500">
                  {t('products.adr.hazardNumber')} {row.adrHazardNumber}
                </span>
              )}
            </div>
            <div className="mt-0.5 max-w-[320px] truncate text-[11px] text-slate-500">
              {row.adrProperShippingName}
              {row.adrTechnicalName ? ` ${row.adrTechnicalName}` : ''}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 align-top">
        {row.ewcCode ? (
          // The default code; the others are counted and listed on hover.
          <div
            title={(row.wasteCodes ?? [])
              .map((w) => `${w.code} ${w.description}${w.isDefault ? ` (${t('products.ewc.default').toLowerCase()})` : ''}`)
              .join('\n') || (row.ewcDescription ?? undefined)}
          >
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[11px] font-bold text-slate-800">{row.ewcCode}</span>
              {row.ewcHazardous && <HazardousBadge />}
              {(row.wasteCodes?.length ?? 0) > 1 && (
                <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-600">
                  +{row.wasteCodes.length - 1}
                </span>
              )}
            </div>
            <div className="mt-0.5 max-w-[260px] truncate text-[11px] text-slate-500">
              {row.ewcDescription}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 align-top">
        {row.grade === 'None' ? (
          <span className="text-slate-400">—</span>
        ) : (
          <span
            className={
              row.grade === 'Food'
                ? 'rounded-sm bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700 ring-1 ring-inset ring-green-200'
                : 'rounded-sm bg-yellow-50 px-1.5 py-0.5 text-[11px] font-medium text-yellow-800 ring-1 ring-inset ring-yellow-200'
            }
          >
            {t(`products.grade.${row.grade}`)}
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 text-right align-top text-slate-700">
        {row.densityKgPerL == null ? (
          <span className="text-slate-400">—</span>
        ) : (
          t('products.kgPerL', { value: row.densityKgPerL })
        )}
      </td>
      <td className="px-3 py-1.5 text-right align-top">
        <button
          type="button"
          aria-label={t('products.ariaEdit', { name: row.name })}
          onClick={onEdit}
          disabled={locked}
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

/** Filter chip — same look as the Resources page. */
function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
